import React, { useState } from 'react';
import { getServiceGroupsOrUsers } from '../../../sources/service';
import { append, pathOr, splitEvery } from 'ramda';
import { connect } from 'react-redux';
import { isNullOrEmpty } from '../../../utils/core';
import { addNotification } from '../../../actions/reduxActions/notification';

import classNames from 'classnames';

/**
 * 
 * @param {String} serviceId
 * @param {String} tenantId
 * @param {String} token
 * @param {String} elementId
 * @param {Array} authorizedUserOrGroups The users or groups that are already authorized to access the flow
 * @param {Function} addUsersOrGroups
 * @param {String} type Will either be "user" or "group"
 * 
 * @description Multiselect search widget for selecting which users or groups are authorized to access a Flow/Swimlane
 */
export const UserGroupSearch = ({ serviceId, tenantId, token, elementId, authorizedUserOrGroups, addUsersOrGroups, onError, type }) => {

    const MAX_NO_RESULTS = 10;

    const [searchResults, updateResults] = useState({ isLoading: false, data: [], hasMoreResults: false });
    const [selections, updateSelections] = useState([]);

    const closeSearch = () => {
        updateResults({ isLoading: false, data: [] });
        updateSelections([]);
    };

    const onSearch = async (value) => {
        if (isNullOrEmpty(value)) {
            updateResults({ ...searchResults, data: [] });
        } else {
            updateResults({ ...searchResults, isLoading: true });
            
            try {
                const searchServiceResults = await getServiceGroupsOrUsers({
                    token,
                    tenantId,
                    serviceId,
                    type,
                    search: value,
                });
        
                const data = (searchServiceResults && searchServiceResults.objectData ? searchServiceResults.objectData : []).map((result) => {
                    const isSelected = authorizedUserOrGroups.some(aug => result.externalId === aug.id);
                    return {
                        ...result,
                        isSelected,
                    };
                });

                if (data.length === 0) {
                    throw new Error('No results were found.');
                }
    
                const hasMoreResults = pathOr(false, ['hasMoreResults'], searchServiceResults);
    
                updateResults({ isLoading: false, data, hasMoreResults });

            } catch ({ message }) {
                onError({
                    type: 'error',
                    message,
                    isPersistent: true,
                });
                updateResults({ isLoading: false, data: [] });
            }
        }

        updateSelections([]);
    };

    const onChange = (e) => {
        const { value, checked } = e.target;
        const result = searchResults.data.find(r => value === r.externalId);

        if (result && checked) {
            const selectionAdded = append(result, selections);
            updateSelections(selectionAdded);
        }

        if (value === result.externalId && !checked) {
            const selectionRemoved = selections.filter(s => s.externalId !== value);
            updateSelections(selectionRemoved);
        }
    };

    const saveSelection = () => {
        addUsersOrGroups(elementId, selections, type);
        closeSearch();
    };

    const debouncedSearch = manywho.utils.debounce(onSearch, 750);

    const containerClasses = classNames({
        'search-form-wrapper': true,
        active: searchResults.isLoading || searchResults.data.length > 0,
    });

    return (
        <div className="search">
            <div className={containerClasses}>
                <div className="form-group">
                    <input
                        placeholder={`Search to add ${type}s`}
                        className="form-control"
                        type="text"
                        onChange={(e) => {

                            // See https://reactjs.org/docs/legacy-event-pooling.html
                            const { value } = e.target;
                            debouncedSearch(value);
                        }}
                        name="search"
                    />
                </div>
                {
                    (searchResults.isLoading || searchResults.data.length > 0) ? (
                        <div className="search-inner">
                            <div className="search-results">
                                {searchResults.isLoading ? (
                                    <div className="wait-container-wrapper">
                                        {React.createElement(
                                            manywho.component.getByName('wait'),
                                            { isVisible: true, message: 'Searching...' },
                                            null,
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="search-results-inner">
                                            { // We want checkboxes to be displayed in two columns of 5
                                                splitEvery(5, searchResults.data.slice(0, MAX_NO_RESULTS)).map((column, i) => (
                                                    <div key={`column-${i}`} className="search-results-column">
                                                        {column.map((groupOrUserOption) => {
                                                            const friendlyNameProperty = groupOrUserOption.properties.find(
                                                                property => property.developerName === 'FriendlyName',
                                                            );
                
                                                            const developerSummaryProperty = groupOrUserOption.properties.find(
                                                                property => property.developerName === 'DeveloperSummary',
                                                            );
                
                                                            const contentOrEmptyString = pathOr('', ['contentValue']);
                
                                                            const friendlyName = contentOrEmptyString(friendlyNameProperty);
                                                            const developerSummary = contentOrEmptyString(developerSummaryProperty);
                
                                                            const optionText = developerSummary === ''
                                                                ? friendlyName
                                                                : `${friendlyName} (${developerSummary})`;
                
                                                            return (
                                                                <div key={groupOrUserOption.externalId} className="checkbox">
                                                                    <label>
                                                                        <input
                                                                            onChange={onChange}
                                                                            type="checkbox"
                                                                            value={groupOrUserOption.externalId}
                                                                            defaultChecked={groupOrUserOption.isSelected}
                                                                            disabled={groupOrUserOption.isSelected}
                                                                        />
                                                                        {optionText}
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        {
                                            searchResults.hasMoreResults || searchResults.data.length > MAX_NO_RESULTS ?
                                                <p>There are more results, please refine your search</p> :
                                                null
                                        }
                                        <div className="search-results-footer">
                                            <div>
                                                <button className="btn btn-default" onClick={closeSearch}>Cancel</button>
                                                <button
                                                    disabled={selections.length === 0}
                                                    className="btn btn-primary"
                                                    onClick={saveSelection}
                                                >
                                                    Add Selected
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : null
                }
            </div>
        </div>
    );
};

const mapDispatchToProps = {
    onError: addNotification,
};

export default connect(null, mapDispatchToProps)(UserGroupSearch);
